import { getLocalizedPrices, type LocalizedPriceEntry } from "@/lib/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";

export type LocalizedPrice = {
  formatted: string;
  currency: string;
  amountMinor: string;
};
export type LocalizedPriceMap = Record<string, LocalizedPrice>;

/** Stripe rejects prices.list when lookup_keys has more than 10 elements. */
const MAX_LOOKUP_KEYS = 10;

/** Fetch catalog prices (SEK, tax-exclusive) for the given lookup_keys. */
export async function fetchLocalizedPrices(lookupKeys: string[]): Promise<LocalizedPriceMap | null> {
  if (typeof window === "undefined") return null;
  if (!lookupKeys.length) return null;

  try {
    const environment = getStripeEnvironment();

    const chunks: string[][] = [];
    for (let i = 0; i < lookupKeys.length; i += MAX_LOOKUP_KEYS) {
      chunks.push(lookupKeys.slice(i, i + MAX_LOOKUP_KEYS));
    }

    const results = await Promise.all(
      chunks.map((chunk) => getLocalizedPrices({ data: { lookupKeys: chunk, environment } })),
    );

    const out: LocalizedPriceMap = {};
    for (const result of results) {
      // A failed chunk must not wipe out the prices resolved by the others.
      if ("error" in result) {
        if (typeof console !== "undefined") console.warn("[stripe-prices]", result.error);
        continue;
      }
      for (const [key, entry] of Object.entries(result) as [string, LocalizedPriceEntry][]) {
        out[key] = {
          formatted: entry.formatted,
          currency: entry.currency,
          amountMinor: String(entry.amountMinor),
        };
      }
    }
    return Object.keys(out).length ? out : null;
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[stripe-prices] fetch failed", err);
    return null;
  }

}

/** Divide a yearly minor-unit amount by 12 for the "~399 kr/mo" hint. */
export function monthlyEquivalent(
  yearly: LocalizedPrice | undefined,
  locale: string = "sv-SE",
): string | null {
  if (!yearly) return null;
  const minor = Number(yearly.amountMinor);
  if (!Number.isFinite(minor) || minor <= 0) return null;
  const perMonth = minor / 12 / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: yearly.currency,
      maximumFractionDigits: 0,
    }).format(perMonth);
  } catch {
    return null;
  }
}
